// Stage 2: court policy hydration.
//
// Calls JTI's GetPolicy SOAP, walks the returned CourtCodelist URIs in
// parallel, parses each as Genericode, and assembles a CourtPolicy. Also
// derives the per-doc metadata allow-list (Map<docCode, allowedCodes[]>)
// that stage 3 uses to suppress optional metadata items the doc's policy
// doesn't list — preventing JTI 4059 "MetaData not defined for <doc>"
// rejections.
//
// Production injects a cached `policyLookup` to avoid repeating the SOAP
// + 1.4MB DOCUMENT list fetch per request. Scripts skip the cache and
// fetch fresh each invocation.
import { XMLParser } from 'fast-xml-parser';
import { emit } from '../../pipeline/trace.js';
import { soapRequest } from './stage-5-transport.js';
import { buildBasicAuthHeader, getJtiCredentials } from './auth.js';
/**
 * Factory: build the policy-hydration stage. If `policyLookup` is provided
 * (production cache), use it; otherwise fetch fresh via SOAP.
 */
export function makePolicyStage(policyLookup) {
    const stage = async (input, ctx) => {
        const county = ctx.courtConfig.county;
        let policy;
        if (policyLookup) {
            emit(ctx.onEvent, {
                traceId: ctx.traceId,
                stage: 'policy',
                level: 'debug',
                event: 'policy_cache_lookup',
                data: { county },
            });
            policy = await policyLookup(county);
        }
        else {
            emit(ctx.onEvent, {
                traceId: ctx.traceId,
                stage: 'policy',
                level: 'info',
                event: 'policy_fetch_start',
                data: { county },
            });
            policy = await fetchPolicyFromJti(county, ctx);
        }
        const docMetadataCodes = new Map(policy.documentTypes.codes.map(d => [d.code, (d.metadata ?? []).map(m => m.code)]));
        const docFilingPartyTags = new Map(policy.documentTypes.codes.map(d => [
            d.code,
            (d.metadata ?? []).find(m => m.code === 'FILING_PARTY')?.additionalInfoTags ?? [],
        ]));
        emit(ctx.onEvent, {
            traceId: ctx.traceId,
            stage: 'policy',
            level: 'info',
            event: 'policy_complete',
            data: { county, docCount: policy.documentTypes.codes.length, totalCodes: countAllCodes(policy) },
        });
        return { state: input.state, policy, docMetadataCodes, docFilingPartyTags };
    };
    Object.defineProperty(stage, 'name', { value: 'policy' });
    Object.defineProperty(stage, 'stageName', { value: 'policy' });
    return stage;
}
function countAllCodes(policy) {
    return Object.values(policy.rawCodeLists).reduce((sum, cl) => sum + cl.codes.length, 0);
}
const ESSENTIAL_CODE_LISTS = ['CASE_CATEGORY', 'CASE_TYPE', 'PARTY_TYPE', 'PARTY_DESIGNATION_TYPE', 'DOCUMENT'];
const xmlParser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    removeNSPrefix: true,
    parseTagValue: false,
    isArray: name => ['CourtCodelist', 'Row', 'Value', 'Column', 'relationship'].includes(name),
});
export async function fetchPolicyFromJti(county, ctx) {
    const body = buildGetPolicyRequest(county);
    const wrapped = wrapPolicySoap(body);
    const response = await soapRequest({ envelope: wrapped, operation: 'GetPolicyRequest', timeoutMs: 60000 }, ctx);
    const refs = extractCodeListRefs(response.xml, ctx.courtConfig.filingReviewUrl);
    emit(ctx.onEvent, {
        traceId: ctx.traceId,
        stage: 'policy',
        level: 'info',
        event: 'policy_codelists_found',
        data: { county, count: refs.length },
    });
    const codeLists = await fetchAndParseCodeLists(refs, ctx);
    return buildCourtPolicy(county, codeLists);
}
export function buildGetPolicyRequest(courtCode) {
    return `<ns4:CourtPolicyQueryMessage
      xmlns:ns1="http://niem.gov/niem/niem-core/2.0"
      xmlns:ns2="urn:oasis:names:tc:legalxml-courtfiling:schema:xsd:CommonTypes-4.0"
      xmlns:ns4="urn:oasis:names:tc:legalxml-courtfiling:schema:xsd:CourtPolicyQueryMessage-4.0">
      <ns2:SendingMDELocationID>
        <ns1:IdentificationID>${escapeXml(courtCode)}</ns1:IdentificationID>
      </ns2:SendingMDELocationID>
    </ns4:CourtPolicyQueryMessage>`;
}
function wrapPolicySoap(body) {
    // GetPolicy uses a plain envelope; namespaces are inline on the inner element.
    return `<?xml version="1.0" encoding="UTF-8"?>
<soap:Envelope xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Header/>
  <soap:Body>
    ${body}
  </soap:Body>
</soap:Envelope>`;
}
export function extractCodeListRefs(xml, filingReviewUrl) {
    const parsed = xmlParser.parse(xml);
    const body = parsed?.Envelope?.Body;
    if (!body) {
        throw new Error('Invalid GetPolicy SOAP response: missing Envelope/Body');
    }
    const message = body.CourtPolicyResponseMessage;
    if (!message) {
        return [];
    }
    const codeLists = message.CourtCodelist ||
        message.RuntimePolicyParameters?.CourtCodelist;
    if (!codeLists || !Array.isArray(codeLists)) {
        return [];
    }
    // GetPolicy may return internal hostnames that aren't publicly accessible.
    // Rewrite to the public hostname derived from the SOAP endpoint.
    const pubBaseUrl = filingReviewUrl ? new URL(filingReviewUrl).origin : null;
    const refs = [];
    for (const cl of codeLists) {
        const elementName = cl.ECFElementName || '';
        const uri = cl.CourtCodelistURI;
        let url = uri?.IdentificationID;
        if (!url || typeof url !== 'string')
            continue;
        if (pubBaseUrl) {
            try {
                const parsed = new URL(url);
                const pubParsed = new URL(pubBaseUrl);
                if (parsed.hostname !== pubParsed.hostname) {
                    parsed.hostname = pubParsed.hostname;
                    url = parsed.toString();
                }
            }
            catch {
                // URL parsing failed, use as-is
            }
        }
        const typeMatch = url.match(/[?&]type=([^&]+)/);
        const type = typeMatch?.[1] || extractTypeFromPath(url);
        if (type) {
            refs.push({ type, url, elementName });
        }
    }
    return refs;
}
function extractTypeFromPath(url) {
    if (url.includes('/documentList'))
        return 'DOCUMENT';
    if (url.includes('/courtLocations'))
        return 'COURT_LOCATIONS';
    if (url.includes('/attorneyList'))
        return 'ATTORNEY_LIST';
    return '';
}
export async function fetchAndParseCodeLists(codeListRefs, ctx) {
    const creds = getJtiCredentials();
    const authHeader = creds ? buildBasicAuthHeader(creds) : undefined;
    const results = {};
    const essentialRefs = codeListRefs.filter(ref => ESSENTIAL_CODE_LISTS.includes(ref.type));
    const otherRefs = codeListRefs.filter(ref => !ESSENTIAL_CODE_LISTS.includes(ref.type) && ref.type !== 'ATTORNEY_LIST');
    const allRefs = [...essentialRefs, ...otherRefs];
    await Promise.all(allRefs.map(async (ref) => {
        try {
            const headers = { Accept: 'application/xml, text/xml' };
            if (authHeader)
                headers.Authorization = authHeader;
            const timeout = ref.type === 'DOCUMENT' ? 45000 : 15000;
            const response = await fetch(ref.url, { headers, signal: AbortSignal.timeout(timeout) });
            if (!response.ok) {
                emit(ctx.onEvent, {
                    traceId: ctx.traceId,
                    stage: 'policy',
                    level: 'warn',
                    event: 'codelist_fetch_error',
                    data: { type: ref.type, status: response.status },
                });
                return;
            }
            const xml = await response.text();
            const parsed = ref.type === 'DOCUMENT' ? parseDocumentListXml(xml) : parseGenericodeXml(xml, ref.type);
            results[ref.type] = parsed;
            if (parsed.codes.length === 0 && xml.length > 100) {
                emit(ctx.onEvent, {
                    traceId: ctx.traceId,
                    stage: 'policy',
                    level: 'warn',
                    event: 'codelist_parse_empty',
                    data: { type: ref.type, xmlLength: xml.length },
                });
            }
            emit(ctx.onEvent, {
                traceId: ctx.traceId,
                stage: 'policy',
                level: 'debug',
                event: 'codelist_fetched',
                data: { type: ref.type, codeCount: parsed.codes.length },
            });
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            emit(ctx.onEvent, {
                traceId: ctx.traceId,
                stage: 'policy',
                level: 'warn',
                event: 'codelist_fetch_error',
                data: { type: ref.type, error: message },
            });
        }
    }));
    return results;
}
export function parseGenericodeXml(xml, listName) {
    const parsed = xmlParser.parse(xml);
    const codeList = findCodeList(parsed);
    if (!codeList) {
        return { name: listName, codes: [] };
    }
    const columns = extractColumns(codeList);
    const simpleCodeList = codeList.SimpleCodeList;
    const rows = simpleCodeList?.Row || [];
    if (!Array.isArray(rows)) {
        return { name: listName, codes: [] };
    }
    const codes = [];
    for (const row of rows) {
        const code = parseRow(row, columns);
        if (code)
            codes.push(code);
    }
    return { name: listName, codes };
}
function findCodeList(parsed) {
    if (parsed.CodeList)
        return parsed.CodeList;
    for (const value of Object.values(parsed)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nested = value;
            if (nested.CodeList)
                return nested.CodeList;
            for (const v2 of Object.values(nested)) {
                if (v2 && typeof v2 === 'object' && !Array.isArray(v2)) {
                    const n2 = v2;
                    if (n2.CodeList)
                        return n2.CodeList;
                }
            }
        }
    }
    return null;
}
function extractColumns(codeList) {
    const columnSet = codeList.ColumnSet;
    if (!columnSet)
        return [];
    const columns = columnSet.Column;
    if (!columns || !Array.isArray(columns))
        return [];
    return columns.map((col) => ({
        id: (col['@_Id'] || col['@_id'] || ''),
        name: (col.ShortName || ''),
    }));
}
function parseRow(row, _columns) {
    const values = row.Value;
    if (!values || !Array.isArray(values))
        return null;
    const attributes = {};
    let code = '';
    let name = '';
    let relationships = [];
    let metadata;
    let formGroups;
    let efmRequiresSubCase;
    for (const val of values) {
        const colRef = (val['@_ColumnRef'] || val['@_columnRef'] || '');
        const simpleValue = val.SimpleValue;
        if (simpleValue != null) {
            const sv = String(simpleValue);
            const colRefLower = colRef.toLowerCase();
            if (colRefLower === 'code' || colRefLower === 'id') {
                code = sv;
            }
            else if (colRefLower === 'name' || colRefLower === 'description' || colRefLower === 'value') {
                name = sv;
            }
            if (colRef)
                attributes[colRef] = sv;
            continue;
        }
        const complexValue = val.ComplexValue;
        if (complexValue) {
            const xsiType = (complexValue['@_type'] || complexValue['@_xsi:type'] || '');
            if (xsiType.includes('ZipCodeValue') || complexValue.zipCode || complexValue.locationCode) {
                const locCode = complexValue.locationCode;
                const locName = complexValue.locationName;
                const zipCode = complexValue.zipCode;
                const caseType = complexValue.caseType;
                if (locCode) {
                    code = String(locCode);
                    name = locName ? String(locName) : code;
                    if (zipCode)
                        attributes['zipCode'] = String(zipCode);
                    if (caseType) {
                        relationships.push({ listName: 'CASE_TYPE', code: String(caseType) });
                    }
                }
            }
            else {
                const cvName = complexValue.name;
                if (cvName)
                    name = String(cvName);
                const rels = complexValue.relationships;
                if (rels) {
                    relationships = parseRelationshipsFromNode(rels);
                }
                const docCaseTypes = complexValue.caseTypes;
                if (docCaseTypes) {
                    for (const ct of extractCodesFromRowBlock(docCaseTypes)) {
                        relationships.push({ listName: 'CASE_TYPE', code: ct });
                    }
                }
                const docCaseCategories = complexValue.caseCategories;
                if (docCaseCategories) {
                    for (const cc of extractCodesFromRowBlock(docCaseCategories)) {
                        relationships.push({ listName: 'CASE_CATEGORY', code: cc });
                    }
                }
                const docMetadata = complexValue.metadata;
                if (docMetadata) {
                    metadata = parseDocumentMetadata(docMetadata);
                }
                const docFormGroups = complexValue.formGroups;
                if (docFormGroups) {
                    formGroups = extractCodesFromRowBlock(docFormGroups);
                }
                const requiresSubCase = complexValue.efmRequiresSubCase;
                if (requiresSubCase === 'true' || requiresSubCase === true) {
                    efmRequiresSubCase = true;
                }
            }
            for (const [k, v] of Object.entries(complexValue)) {
                if (k !== 'name' &&
                    k !== 'relationships' &&
                    k !== 'caseTypes' &&
                    k !== 'caseCategories' &&
                    k !== 'metadata' &&
                    k !== 'formGroups' &&
                    k !== 'efmRequiresSubCase' &&
                    k !== 'locationCode' &&
                    k !== 'locationName' &&
                    k !== 'zipCode' &&
                    k !== 'caseType' &&
                    !k.startsWith('@_') &&
                    typeof v === 'string') {
                    attributes[k] = v;
                }
            }
        }
    }
    if (!code)
        return null;
    if (relationships.length === 0) {
        relationships = parseRelationships(row);
    }
    return { code, name: name || code, relationships, attributes, metadata, formGroups, efmRequiresSubCase };
}
/** Exported for direct unit testing (deep path only — not on the pipeline
 *  public surface). */
export function parseDocumentMetadata(metadataNode) {
    const items = metadataNode.metadataItems;
    if (!items)
        return [];
    const arr = Array.isArray(items) ? items : [items];
    return arr
        .filter((item) => item != null && typeof item === 'object')
        .map(item => {
        const tags = item.additionalInfoTags;
        let additionalInfoTags = [];
        if (Array.isArray(tags)) {
            additionalInfoTags = tags.filter((t) => typeof t === 'string');
        }
        else if (typeof tags === 'string') {
            additionalInfoTags = [tags];
        }
        return {
            code: String(item.code || ''),
            name: String(item.name || ''),
            description: item.description ? String(item.description) : undefined,
            multiple: item.multiple === 'true' || item.multiple === true,
            classType: String(item.classType || ''),
            filter: item.filter ? String(item.filter) : undefined,
            subType: item.subType ? String(item.subType) : undefined,
            valueRestriction: item.valueRestriction ? String(item.valueRestriction) : undefined,
            additionalInfoTags,
            required: item.required === 'true' || item.required === true,
        };
    })
        .filter(m => m.code);
}
function extractCodesFromRowBlock(block) {
    const rows = block.Row;
    if (!rows)
        return [];
    const arr = Array.isArray(rows) ? rows : [rows];
    const codes = [];
    for (const row of arr) {
        if (!row || typeof row !== 'object')
            continue;
        const values = row.Value;
        if (!values)
            continue;
        const valArr = Array.isArray(values) ? values : [values];
        for (const val of valArr) {
            if (!val || typeof val !== 'object')
                continue;
            const sv = val.SimpleValue;
            if (sv != null)
                codes.push(String(sv));
        }
    }
    return codes;
}
function parseRelationshipsFromNode(rels) {
    const relArray = rels.relationship;
    if (!relArray)
        return [];
    const arr = Array.isArray(relArray) ? relArray : [relArray];
    return arr
        .filter((r) => r != null && typeof r === 'object')
        .map(r => ({
        listName: String(r.relatedListName || ''),
        code: String(r.relatedCode || ''),
    }))
        .filter(r => r.listName && r.code);
}
function parseRelationships(row) {
    const relationships = row.relationships;
    if (!relationships)
        return [];
    const rels = relationships.relationship;
    if (!rels)
        return [];
    const relArray = Array.isArray(rels) ? rels : [rels];
    return relArray
        .filter((r) => r != null && typeof r === 'object')
        .map(r => ({
        listName: (r.relatedListName || ''),
        code: (r.relatedCode || ''),
    }))
        .filter(r => r.listName && r.code);
}
export function parseDocumentListXml(xml) {
    const result = parseGenericodeXml(xml, 'DOCUMENT');
    if (result.codes.length > 0)
        return result;
    try {
        const data = JSON.parse(xml);
        if (Array.isArray(data)) {
            return {
                name: 'DOCUMENT',
                codes: data
                    .map((item) => ({
                    code: item.code || item.id || '',
                    name: item.name || item.description || '',
                    relationships: [],
                    attributes: item,
                }))
                    .filter((c) => c.code),
            };
        }
    }
    catch {
        // Not JSON
    }
    return { name: 'DOCUMENT', codes: [] };
}
function deduplicateCodeList(list) {
    const byCode = new Map();
    for (const code of list.codes) {
        const existing = byCode.get(code.code);
        if (existing) {
            for (const rel of code.relationships) {
                if (!existing.relationships.some(r => r.listName === rel.listName && r.code === rel.code)) {
                    existing.relationships.push(rel);
                }
            }
        }
        else {
            byCode.set(code.code, { ...code, relationships: [...code.relationships] });
        }
    }
    return { name: list.name, codes: [...byCode.values()] };
}
function extractZipCourtMap(list) {
    const mappings = [];
    for (const code of list.codes) {
        const zipCode = code.attributes['zipCode'];
        if (zipCode && code.code) {
            const caseTypeRel = code.relationships.find(r => r.listName === 'CASE_TYPE');
            mappings.push({
                zipCode,
                locationCode: code.code,
                locationName: code.name,
                caseType: caseTypeRel?.code,
            });
        }
    }
    return mappings;
}
export function buildCourtPolicy(courtCode, codeLists) {
    return {
        courtCode,
        caseCategories: codeLists['CASE_CATEGORY'] || { name: 'CASE_CATEGORY', codes: [] },
        caseTypes: codeLists['CASE_TYPE'] || { name: 'CASE_TYPE', codes: [] },
        documentTypes: codeLists['DOCUMENT'] || { name: 'DOCUMENT', codes: [] },
        partyTypes: codeLists['PARTY_TYPE'] || { name: 'PARTY_TYPE', codes: [] },
        filingStatuses: codeLists['FILING_STATUS'] || { name: 'FILING_STATUS', codes: [] },
        crossReferenceTypes: codeLists['CROSS_REFERENCE'] || { name: 'CROSS_REFERENCE', codes: [] },
        courtLocations: deduplicateCodeList(codeLists['COURT_LOCATIONS'] || { name: 'COURT_LOCATIONS', codes: [] }),
        zipToCourtMap: extractZipCourtMap(codeLists['COURT_LOCATIONS'] || { name: 'COURT_LOCATIONS', codes: [] }),
        rawCodeLists: codeLists,
    };
}
function escapeXml(str) {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}
